import { useMemo, useState } from 'react';

import { useFilters } from '@spotify/confidence-core-react';
import type { CollectionEntryFragment } from '@spotify/confidence-graphql';

import {
  type CollectionEntryExtensionOptions,
  findCollectionEntryExtension,
  getExtensionDisplayName,
} from '../../extensions';

const ALL_TYPES = 'all';

const GENERIC_COLUMNS = [
  'favorite',
  'name',
  'type',
  'owner',
  'labels',
  'added',
] as const;

export const useCollectionEntriesFilter = (
  entries: CollectionEntryFragment[] | undefined,
  extensions: CollectionEntryExtensionOptions[],
) => {
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const { searchQuery, setQuery } = useFilters();

  const availableTypes = useMemo(() => {
    if (!entries) return [];
    const types = new Set<string>();
    for (const entry of entries) {
      const extension = findCollectionEntryExtension(extensions, entry);
      if (extension) types.add(extension.resourceType);
    }
    return [...types]
      .sort((a, b) => a.localeCompare(b))
      .map(type => ({ id: type, label: type }));
  }, [entries, extensions]);

  const availableTypeIds = availableTypes.map(t => t.id);

  const effectiveTypeFilter = useMemo(() => {
    if (availableTypes.length === 1) return availableTypes[0].id;
    if (typeFilter !== ALL_TYPES && !availableTypeIds.includes(typeFilter)) {
      return ALL_TYPES;
    }
    return typeFilter;
  }, [availableTypes, typeFilter, availableTypeIds]);

  const activeExtension = useMemo(
    () =>
      effectiveTypeFilter !== ALL_TYPES
        ? extensions.find(ext => ext.resourceType === effectiveTypeFilter)
        : undefined,
    [extensions, effectiveTypeFilter],
  );

  const extensionColumns = activeExtension?.columns;

  const allColumnIds = useMemo(() => {
    const ids: string[] = [...GENERIC_COLUMNS];
    if (extensionColumns) {
      const typeIndex = ids.indexOf('type');
      const extensionIds = extensionColumns.map(col => col.id);
      ids.splice(typeIndex + 1, 0, ...extensionIds);
    }
    return ids;
  }, [extensionColumns]);

  const activeColumns = useMemo(
    () => extensionColumns?.filter(col => !hiddenColumns.has(col.id)),
    [extensionColumns, hiddenColumns],
  );

  const filteredEntries = useMemo(() => {
    if (!entries) return [];

    let result = entries;

    if (effectiveTypeFilter !== ALL_TYPES) {
      result = result.filter(entry => {
        const ext = findCollectionEntryExtension(extensions, entry);
        const type = ext?.resourceType ?? 'Unknown';
        return type === effectiveTypeFilter;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(entry => {
        const ext = findCollectionEntryExtension(extensions, entry);
        const name = entry.resource
          ? getExtensionDisplayName(ext, entry.resource, entry)
          : entry.name;
        return name.toLowerCase().includes(query);
      });
    }

    return result;
  }, [entries, effectiveTypeFilter, searchQuery, extensions]);

  const selectType = (type: string) => {
    setTypeFilter(type);
    setHiddenColumns(new Set());
  };

  const toggleColumn = (columnId: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  };

  const isColumnVisible = (columnId: string) => !hiddenColumns.has(columnId);

  const hasActiveFilters = searchQuery || effectiveTypeFilter !== ALL_TYPES;

  return {
    filteredEntries,
    searchQuery,
    setQuery,
    typeFilter,
    availableTypes,
    selectType,
    allColumnIds,
    activeColumns,
    hiddenColumns,
    toggleColumn,
    isColumnVisible,
    hasActiveFilters,
  };
};
